import React, { useState, useEffect, useCallback } from 'react';
import { Search, Building2, MapPin, Check, AlertCircle } from 'lucide-react';
import { Globe } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { LoadingState } from '@/components/ui/LoadingState';
import { VStack, HStack } from '@/components/ui/Stack';
import { VenuesService, type Venue } from '@/services/VenuesService';
import { GooglePlacesSearchService, type HotelSearchResult } from '@/services/GooglePlacesSearchService';
import { useNotificationStore } from '@/stores/notificationStore';
import { apiFetch } from '@/lib/api';

interface VenueSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (venue: Venue) => void;
  title?: string;
  eventId?: string; // Required if we need to import and connect
  cventSupplierId?: string; // Required if we need to connect after import
  onVenueImported?: () => void; // Callback when venue is imported
  initialSearchTerm?: string; // Pre-populate search field and auto-search
}

interface SearchResultWithMatch extends HotelSearchResult {
  matchedVenue?: {
    id: string;
    property_name: string;
  };
}

export function VenueSearchModal({
  isOpen,
  onClose,
  onSelect,
  title = 'Find Matching Venue',
  eventId,
  cventSupplierId,
  onVenueImported,
  initialSearchTerm,
}: VenueSearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResultWithMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  
  const venuesService = new VenuesService();
  const placesService = new GooglePlacesSearchService();
  const notificationStore = useNotificationStore();

  const handleSearch = useCallback(async (searchQuery?: string) => {
    const query = searchQuery || searchTerm.trim();
    if (!query) return;
    
    setLoading(true);
    setHasSearched(true);
    setError(null);
    
    try {
      // Search Google Places API for hotels
      const searchResponse = await placesService.searchHotels({
        query: query,
        pageSize: 20,
      });

      // Check which place_ids already exist in our database
      const placeIds = searchResponse.hotels.map(h => h.placeId).filter(Boolean);
      let existingVenues: Record<string, { id: string; property_name: string }> = {};
      
      if (placeIds.length > 0) {
        try {
          const checkResponse = await apiFetch('/api/google-places/check-existing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ placeIds }),
          });
          
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            existingVenues = checkData.data.existing || {};
          }
        } catch (err) {
          console.error('Failed to check existing venues:', err);
          // Continue even if check fails
        }
      }

      // Combine Google Places results with database matches
      const combinedResults: SearchResultWithMatch[] = searchResponse.hotels.map(hotel => ({
        ...hotel,
        matchedVenue: existingVenues[hotel.placeId] ? {
          id: existingVenues[hotel.placeId].id,
          property_name: existingVenues[hotel.placeId].property_name,
        } : undefined,
      }));

      setResults(combinedResults);
    } catch (err) {
      console.error('Failed to search venues:', err);
      setError(err instanceof Error ? err.message : 'Failed to search venues');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, placesService]);
  
  // Wrapper to handle search with current searchTerm
  const performSearch = useCallback(() => {
    handleSearch();
  }, [handleSearch]);

  // Search on enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Reset state when opening and auto-search if initialSearchTerm provided
  useEffect(() => {
    if (isOpen) {
      const initialTerm = initialSearchTerm || '';
      setSearchTerm(initialTerm);
      setResults([]);
      setHasSearched(false);
      setLoading(false);
      setError(null);
      
      // Auto-trigger search if initialSearchTerm is provided
      if (initialTerm.trim()) {
        // Small delay to ensure modal is fully rendered
        const timeoutId = setTimeout(() => {
          handleSearch(initialTerm);
        }, 100);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isOpen, initialSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle selecting a venue from Google Places result
  async function handleSelectResult(result: SearchResultWithMatch) {
    if (result.matchedVenue) {
      // Venue exists in database - fetch full venue data and select it
      try {
        const venue = await venuesService.getById(result.matchedVenue.id);
        onSelect(venue);
        onClose();
      } catch (err) {
        console.error('Failed to fetch venue:', err);
        setError('Failed to load venue details');
      }
    } else {
      // Venue doesn't exist in database - import it, trigger enrichment, then connect
      if (!eventId || !cventSupplierId) {
        setError('Cannot import venue: missing event or Cvent connection information');
        return;
      }

      setImportingIds(prev => new Set(prev).add(result.placeId));
      setError(null);

      try {
        // Import venue from Google Places
        const importResult = await placesService.importHotels([{
          placeId: result.placeId,
          name: result.name,
          address: result.address,
          lat: result.lat,
          lon: result.lon,
          rating: result.rating,
          userRatingCount: result.userRatingCount,
          phone: result.phone,
          website: result.website,
        }]);

        if (importResult.imported === 0) {
          // Venue already exists (race condition) - try to find it
          const checkResponse = await apiFetch('/api/google-places/check-existing', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ placeIds: [result.placeId] }),
          });
          
          if (checkResponse.ok) {
            const checkData = await checkResponse.json();
            const existing = checkData.data.existing[result.placeId];
            if (existing) {
              const venue = await venuesService.getById(existing.id);
              onSelect(venue);
              onClose();
              return;
            }
          }
          
          setError('Failed to import venue. It may already exist.');
          return;
        }

        const venueId = importResult.importedIds[0];
        if (!venueId) {
          setError('Failed to import venue: no venue ID returned');
          return;
        }

        // Trigger enrichment (AI research) - happens asynchronously
        try {
          await venuesService.triggerEnrichment(venueId);
        } catch (enrichError) {
          // Log but don't fail - enrichment will happen eventually
          console.warn('Failed to trigger enrichment immediately:', enrichError);
        }

        // Fetch the newly created venue
        const venue = await venuesService.getById(venueId);
        
        // Show success notification
        notificationStore.success(`Imported ${result.name} and triggered AI research`, {
          title: 'Venue Imported',
        });
        
        // Notify parent that venue was imported (so it can refresh)
        onVenueImported?.();
        
        // Select the venue (which will connect it to Cvent and add to shortlist)
        onSelect(venue);
        onClose();
      } catch (err) {
        console.error('Failed to import venue:', err);
        setError(err instanceof Error ? err.message : 'Failed to import venue. Please try again.');
      } finally {
        setImportingIds(prev => {
          const next = new Set(prev);
          next.delete(result.placeId);
          return next;
        });
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
    >
      <VStack gap={4} className="min-h-[400px]">
        <HStack gap={2}>
          <Input
            placeholder="Search hotel name (e.g., 'JW Marriott Mexico City')..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            leftIcon={<Search className="w-4 h-4" />}
            disabled={loading}
          />
          <Button
            onClick={() => handleSearch()}
            isLoading={loading}
            disabled={!searchTerm.trim() || loading}
          >
            Search
          </Button>
        </HStack>

        {error && (
          <div className="bg-error-50 border border-error-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error-700">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto border rounded-md min-h-[300px] max-h-[400px]">
          {loading ? (
            <div className="h-full flex items-center justify-center py-12">
              <LoadingState text="Searching Google Places..." />
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y">
              {results.map((result) => {
                const isImporting = importingIds.has(result.placeId);
                const isInDatabase = !!result.matchedVenue;
                
                return (
                  <button
                    key={result.placeId}
                    className="w-full text-left p-4 hover:bg-gray-50 transition-colors flex items-center justify-between group"
                    onClick={() => handleSelectResult(result)}
                    disabled={isImporting}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {isInDatabase ? (
                        <Building2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-600" />
                      ) : (
                        <Globe className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-medium text-gray-900 truncate">{result.name}</div>
                          {isInDatabase && (
                            <Badge variant="success" className="text-xs flex-shrink-0">
                              In Database
                            </Badge>
                          )}
                          {!isInDatabase && (
                            <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{result.address}</span>
                        </div>
                        {isInDatabase && result.matchedVenue && (
                          <div className="text-xs text-gray-400">
                            Matches: {result.matchedVenue.property_name}
                          </div>
                        )}
                        {!isInDatabase && (
                          <div className="text-xs text-gray-400">
                            Will be imported and connected
                          </div>
                        )}
                        {result.rating && (
                          <div className="text-xs text-gray-400 mt-1">
                            ⭐ {result.rating.toFixed(1)} {result.userRatingCount ? `(${result.userRatingCount.toLocaleString()} reviews)` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2">
                      <Button 
                        size="sm" 
                        variant="primary" 
                        rightIcon={<Check className="w-4 h-4" />}
                        isLoading={isImporting}
                        disabled={isImporting}
                      >
                        {isImporting ? 'Importing...' : isInDatabase ? 'Select' : 'Import & Connect'}
                      </Button>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : hasSearched ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-2 py-12">
              <Search className="w-8 h-8 opacity-20" />
              <p>No hotels found matching "{searchTerm}"</p>
              <p className="text-xs text-gray-400">Try a different search term</p>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-2 py-12">
              <Search className="w-8 h-8 opacity-20" />
              <p className="text-sm">Search for a hotel name to find matching venues</p>
              <p className="text-xs text-gray-400">Results will show which hotels are already in your database</p>
            </div>
          )}
        </div>
      </VStack>
    </Modal>
  );
}

